import { Injectable, OnDestroy } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { APICoreService } from 'src/app/common/services/api-core/api-core.service';
import { EnvironmentService } from 'src/app/common/services/environment/environment.service';
import { FileDTO } from '../../dtos/file.dto';
import {
  ChatMessage,
  ConversationResponse,
  GenerateOptionsResponse,
  GlaiUserFeedbackPayload,
  TokenData,
} from '../../dtos/glai.dto';

@Injectable({
  providedIn: 'root',
})
export class GlaiService implements OnDestroy {
  private messageSubject = new Subject<ChatMessage>();

  public messages$ = this.messageSubject.asObservable();

  private eventSource: EventSource | null = null;

  private currentMessages = new Map<string, ChatMessage>();

  error: string | null = null;

  constructor(private apiService: APICoreService) {}

  addUserMessage(content: string, uploadedFiles: FileDTO[]): void {
    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      content,
      isComplete: true,
      timestamp: new Date().toISOString(),
      role: 'user',
      files: uploadedFiles,
    };
    this.messageSubject.next(userMessage);
  }

  startConversation(
    message: string,
    messageFiles?: FileDTO[],
    modes?: string[]
  ): Observable<ConversationResponse> {
    return this.apiService.postJSONRequest('ai/conversation', {
      message,
      files: messageFiles,
      modes,
    });
  }

  continueConversation(
    conversationId: number,
    message: string,
    messageFiles?: FileDTO[],
    modes?: string[]
  ): Observable<unknown> {
    return this.apiService.postJSONRequest(
      `ai/conversation/${conversationId}/message`,
      { message, files: messageFiles, modes }
    );
  }

  connectToStream(streamId: string): void {
    this.disconnectStream();

    const glaiApiUrl = EnvironmentService.glaiApiUrl();
    const streamUrl = `${glaiApiUrl}/chatbot/stream/${streamId}`;

    this.eventSource = new EventSource(streamUrl);

    this.eventSource.addEventListener('token', (event: MessageEvent) => {
      try {
        const tokenData: TokenData = JSON.parse(event.data);
        this.handleTokenMessage(tokenData);
      } catch (error) {
        this.error = `${error}`;
      }
    });

    this.eventSource.addEventListener('error', (event: MessageEvent) => {
      try {
        const tokenData: TokenData = JSON.parse(event.data);
        this.handleTokenMessage(tokenData);
      } catch (error) {
        this.error = `${error}`;
      }
    });

    const handleStreamComplete = () => {
      this.markIncompleteMessagesComplete();
    };
    ['done', 'complete'].forEach((eventName) => {
      this.eventSource?.addEventListener(eventName, handleStreamComplete);
    });

    this.eventSource.onerror = (error) => {
      this.error = `${error}`;
    };
  }

  private handleTokenMessage(tokenData: TokenData): void {
    const messageId = tokenData.message_id;

    if (this.currentMessages.has(messageId)) {
      const existingMessage = this.currentMessages.get(messageId);
      if (existingMessage) {
        existingMessage.rawContent =
          (existingMessage.rawContent || '') + tokenData.content;
        GlaiService.extractThinking(existingMessage);
        this.messageSubject.next({ ...existingMessage });
      }
    } else {
      const newMessage: ChatMessage = {
        id: messageId,
        rawContent: tokenData.content,
        content: tokenData.content,
        isComplete: false,
        timestamp: tokenData.timestamp,
        role: 'assistant',
      };
      GlaiService.extractThinking(newMessage);
      this.currentMessages.set(messageId, newMessage);
      this.messageSubject.next(newMessage);
    }
  }

  private markIncompleteMessagesComplete(): void {
    this.currentMessages.forEach((message) => {
      if (!message.isComplete) {
        message.isComplete = true;
        this.messageSubject.next({ ...message });
      }
    });
  }

  static extractThinking(message: ChatMessage): void {
    const source = message.rawContent || message.content;
    const thinkingParts = Array.from(
      source.matchAll(/<thinking>(.*?)<\/thinking>/gs)
    ).map((match) => match[1].trim());
    if (thinkingParts.length > 0) {
      message.thinking = thinkingParts.join('\n\n');
    }
    message.content = source.replace(/<thinking>.*?<\/thinking>/gs, '').trim();

    if (source.includes('<thinking>') && !source.includes('</thinking>')) {
      const parts = source.split('<thinking>');
      message.content = parts[0].trim();
      message.thinking = parts[1].trim();
    }
  }

  markMessageComplete(messageId: string): void {
    if (this.currentMessages.has(messageId)) {
      const message = this.currentMessages.get(messageId);
      if (message) {
        message.isComplete = true;
        this.messageSubject.next({ ...message });
      }
    }
  }

  disconnectStream(): void {
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }
  }

  generateEgroweOptions(
    competencyId: number,
    strandId: number,
    level: number
  ): Observable<GenerateOptionsResponse> {
    return this.apiService.getRequest('ai/generate-content/egrowe-options', {
      competencyId,
      strandId,
      level,
    });
  }

  submitUserFeedback(
    conversationId: number,
    payload: GlaiUserFeedbackPayload
  ): Observable<unknown> {
    return this.apiService.postJSONRequest(
      `ai/conversation/${conversationId}/feedback`,
      payload
    );
  }

  ngOnDestroy(): void {
    this.disconnectStream();
  }
}
